import { CheckOutlined, CloseOutlined, RedoOutlined, UndoOutlined } from '@ant-design/icons';
import { message, Modal } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { analytics } from '../../analytics/analytics';
import { AnalyticsEvent } from '../../analytics/analyticsEvent';
import { updateTask } from '../../api/task.api';
import { AIIcon } from '../../assets/CustomIcons/AIIcon';
import { useAIDescriptionChat } from '../../hooks/useAIDescriptionChat';
import { queryClient } from '../../queryClient';
import { queryKeys } from '../../serverState/queryKeys';
import { mergeTaskListRow } from '../../serverState/tasks';
import { optimisticUpdateListItem } from '../../serverState/helpers/optimisticCache';
import { Epic } from '../../types/Epic';
import Task from '../../types/Task';
import { TaskType } from '../../types/TaskType';
import { useThemeStore } from '../../stores/themeStore';
import { BaseButton } from '../UI/BaseButton';
import { TaskTypeLabel } from '../UI/Dropdown/TaskTypeDropdown';
import { AIDescriptionChat } from './AITaskDescription/components/AIDescriptionChat';
import { AIDescriptionEditor } from './AITaskDescription/components/AIDescriptionEditor';
import { UnsavedChangesModal } from './UnsavedChangesModal';

interface AITaskDescriptionModalProps {
  task: Task | Epic;
  open: boolean;
  onClose: () => void;
  /** Receives the saved task so the caller can patch its own caches (#3010). */
  onSave?: (task: Task) => void;
  usersWithAccessToSpace?: Array<{
    id: number;
    fullName?: string | null;
    email: string;
    profilePicturePath?: string | null;
  }>;
}

export const AITaskDescriptionModal: React.FC<AITaskDescriptionModalProps> = ({
  task,
  open,
  onClose,
  onSave,
  usersWithAccessToSpace = [],
}) => {
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
  const [messageApi, contextHolder] = message.useMessage();
  const [isSaving, setIsSaving] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const chatInputFocusTriggerRef = React.useRef<() => void>();
  const wasLoadingRef = React.useRef(false);

  const chat = useAIDescriptionChat({
    workspaceId: task?.workspaceId || 0,
    taskTitle: task?.title || '',
    taskType: task?.type || 'task',
    initialDescription: task?.description || '',
    onDescriptionChange: () => {
      // Description changed in editor
    },
  });

  // When the AI finishes answering (loading true → false), return focus to the
  // chat input so the user can keep chatting without clicking back in. #2410
  useEffect(() => {
    if (wasLoadingRef.current && !chat.isLoading && open) {
      chatInputFocusTriggerRef.current?.();
    }
    wasLoadingRef.current = chat.isLoading;
  }, [chat.isLoading, open]);

  // Track if there are unsaved changes
  useEffect(() => {
    if (task) {
      const hasChanges = chat.description !== task.description;
      setHasUnsavedChanges(hasChanges);
    }
  }, [chat.description, task]);

  // Reset chat when modal opens
  useEffect(() => {
    if (open) {
      chat.resetChat();
    }
  }, [open, chat.resetChat]);

  const handleModalClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const handleSave = useCallback(async () => {
    if (!task) return;

    setIsSaving(true);
    try {
      // Update local state immediately for instant feedback
      const updatedTask = {
        ...task,
        description: chat.description,
      };
      // Update task in cache
      if (task?.type === TaskType.epic) {
        // Epic rows carry no `key` (serverState/epics.ts stamps none), so the merge's only
        // effect here is its `?? incoming.id` fallback adding `key: <id>` to the edited row —
        // harmless, and what the list consumers would want anyway. It is used so both branches
        // stay one code path and an epics row that later grows list-only fields can't be
        // blanked by a detail-record replace. (#3010)
        optimisticUpdateListItem<Task>(queryClient, queryKeys.epics(task.workspaceId), updatedTask, mergeTaskListRow);
      } else {
        // `task` is Task View's DETAIL record, which carries no `key` — useGetTasks
        // stamps that on list rows only. A wholesale replace here would write
        // `key: undefined` into ['tasks', ws], and that cache is staleTime 24h, so the
        // poisoned row would outlive the session and break planning row selection
        // (SprintPlanningTable) and column width bucketing (textMeasurement). (#3010)
        optimisticUpdateListItem<Task>(queryClient, queryKeys.tasks(task.workspaceId), updatedTask, mergeTaskListRow);
      }

      // Then update via API
      await updateTask(task.id, updatedTask);
      void messageApi.success('Description updated with Vibe Speccing');

      // Track AI completion analytics
      const attempts = chat.messages.filter((msg) => msg.role === 'user').length;
      analytics.track(AnalyticsEvent.aiCompleted, {
        taskId: task.id,
        feature: 'vibe_speccing',
        attempts,
      });

      // Call parent onSave callback if provided. Pass the task that was actually saved
      // — the caller needs the new description to patch caches this modal doesn't know
      // about (the task detail record). (#3010)
      onSave?.(updatedTask);

      // Close modal
      onClose();
    } catch (error) {
      void messageApi.error('Failed to save description');
      console.error('Failed to save description:', error);
    } finally {
      setIsSaving(false);
    }
  }, [task, chat.description, chat.messages, messageApi, onSave, onClose]);

  const handleSaveAndExit = useCallback(async () => {
    setShowUnsavedModal(false);
    await handleSave();
  }, [handleSave]);

  const handleDiscardAndExit = useCallback(() => {
    setShowUnsavedModal(false);
    onClose();
  }, [onClose]);

  const handleCancelModal = useCallback(() => {
    setShowUnsavedModal(false);
  }, []);

  const handleSendPrompt = useCallback(
    (prompt: string) => {
      // Send any predefined prompt directly to the AI
      chat.sendMessage(prompt);
    },
    [chat]
  );

  return (
    <>
      {contextHolder}

      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        open={showUnsavedModal}
        onDiscard={handleDiscardAndExit}
        onCancel={handleCancelModal}
        onSave={handleSaveAndExit}
        isSaving={isSaving || chat.isLoading}
      />

      <Modal
        title={
          <div className="flex items-center gap-2">
            {/* Vibe Speccing Badge */}
            <div
              className="flex items-center gap-1 px-2 py-1 pl-1 rounded cursor-default relative"
              style={{ background: 'linear-gradient(135deg, #7FF5AA 0%, #08CF65 100%)' }}
            >
              <div
                className="absolute inset-0 rounded pointer-events-none"
                style={{ boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.25) inset' }}
              />
              <AIIcon className="text-white" />
              <span className="text-sm font-semibold text-white">Vibe Speccing</span>
            </div>

            {/* Optimized for */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-medium leading-3 text-brand-grey-dark">Optimized for:</span>
              <div className="px-1 rounded border border-solid border-neutral-300">
                <TaskTypeLabel type={(task?.type || TaskType.task) as TaskType} size="medium" />
              </div>
            </div>
          </div>
        }
        open={open}
        onCancel={handleModalClose}
        afterOpenChange={(isOpen) => {
          // Focus chat input after modal opens
          if (isOpen && chatInputFocusTriggerRef.current) {
            chatInputFocusTriggerRef.current();
          }
        }}
        centered
        width={1250}
        closeIcon={<CloseOutlined style={{ fontSize: 16 }} />}
        footer={null}
        styles={{
          header: { padding: '16px 32px 16px', borderBottom: '1px solid var(--color-neutral-300)', marginBottom: 0 },
          body: { padding: 0, height: '80vh', display: 'flex', flexDirection: 'column', position: 'relative' },
        }}
        className="[&_.ant-modal-content]:!p-0"
      >
        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Side - Chat */}
          <div
            className="w-[40%] border-r border-neutral-300 flex flex-col overflow-clip"
            style={{
              backgroundImage:
                resolvedTheme === 'dark'
                  ? 'linear-gradient(142.778deg, #0e3a1e 0%, #0a2614 22.273%, rgba(10, 38, 20, 0.2) 48.599%, #0a2614 72.48%, #145229 100%), linear-gradient(90deg, #0e1114 0%, #0e1114 100%)'
                  : 'linear-gradient(142.778deg, #ADFFC9 0%, #E6FFED 22.273%, rgba(230, 255, 237, 0.2) 48.599%, #E6FFED 72.48%, #7FF5AA 100%), linear-gradient(90deg, #FFFFFF 0%, #FFFFFF 100%)',
            }}
          >
            {/* Chat Messages */}
            <div className="flex-1 overflow-hidden">
              <AIDescriptionChat
                messages={chat.messages}
                isLoading={chat.isLoading}
                userInput={chat.userInput}
                onUserInputChange={chat.setUserInput}
                onSendMessage={chat.sendMessage}
                disabled={isSaving}
                selectedContext={chat.selectedContext}
                onClearContext={chat.clearSelectedContext}
                hasInitialDescription={!!(task?.description && task.description.trim())}
                currentDescription={chat.description}
                onSendPrompt={handleSendPrompt}
                focusTriggerRef={chatInputFocusTriggerRef}
              />
            </div>
          </div>

          {/* Right Side - Description Editor */}
          <div className="w-[60%] bg-brand-white flex flex-col">
            {/* Editor */}
            <div className="px-4 flex-1 overflow-auto">
              <div className="pt-2 justify-start text-brand-grey-dark text-xl font-semibold line-clamp-1">
                {task.title}
              </div>

              <AIDescriptionEditor
                value={chat.description}
                onChange={chat.setDescription}
                readOnly={chat.isLoading || isSaving}
                usersWithAccessToSpace={usersWithAccessToSpace}
                onAddSelectionToContext={(selectedText) => {
                  chat.setSelectedContext(selectedText);
                  // Focus chat input after adding selection to context
                  if (chatInputFocusTriggerRef.current) {
                    chatInputFocusTriggerRef.current();
                  }
                }}
              />
            </div>

            {/* Bottom part */}
            <div className="flex justify-between p-3 bg-brand-white">
              {/* Undo/Redo Buttons - Bottom Left */}
              <div className="flex gap-2">
                <BaseButton
                  type="secondary"
                  size="medium"
                  onClick={chat.undo}
                  disabled={!chat.canUndo || isSaving || chat.isLoading}
                >
                  <UndoOutlined />
                </BaseButton>
                <BaseButton
                  type="secondary"
                  size="medium"
                  onClick={chat.redo}
                  disabled={!chat.canRedo || isSaving || chat.isLoading}
                >
                  <RedoOutlined />
                </BaseButton>
              </div>
              <BaseButton type="primary" size="large" onClick={handleSave} disabled={isSaving || chat.isLoading}>
                <CheckOutlined />
                {chat.isLoading ? 'Generating...' : isSaving ? 'Saving...' : 'Save Description'}
              </BaseButton>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
